import { SeasonTeamNode } from "@/features/credentials/components/season-team-node";
import { SeasonTeamRow } from "@/features/credentials/components/season-team-row";
import { addSelectedIds, removeSelectedIds } from "@/features/credentials/lib/toggle-selected-ids";
import type { SeasonTeam } from "@/features/credentials/models/season-team-tree";
import { useEntitiesListQuery } from "@/features/credentials/queries/use-entities-list";
import { useSeasonTeamsTreeQuery } from "@/features/credentials/queries/use-season-teams-tree";
import { Badge, Button, Input, ScrollArea } from "@cmde/shadcn";
import { Loader2Icon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export function SeasonTeamTree({
  sport,
  organizationId,
  competitionIds,
  value,
  onChange,
  disabled,
}: {
  sport: string;
  organizationId: string;
  competitionIds: string[];
  value: string[];
  onChange: (nextIds: string[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [pendingSelectAll, setPendingSelectAll] = useState<Record<string, boolean>>({});
  const seasonIdsKeyRef = useRef("");

  const treeQuery = useSeasonTeamsTreeQuery({
    sport,
    organizationId,
    competitionIds,
    expandedSeasonIds: [...expandedIds],
    enabled: !disabled,
  });
  const seasons = treeQuery.groups;
  const seasonIdsKey = seasons.map((season) => season.seasonId).join(",");

  useEffect(() => {
    if (seasonIdsKeyRef.current === seasonIdsKey) {
      return;
    }

    seasonIdsKeyRef.current = seasonIdsKey;
    setExpandedIds(new Set());
    setPendingSelectAll({});
    setSearch("");
  }, [seasonIdsKey]);

  const loadedTeamIds = useMemo(
    () => new Set(seasons.flatMap((season) => season.teams.map((team) => team.entityId))),
    [seasons],
  );
  const loadedTeamNamesById = useMemo(
    () =>
      new Map(
        seasons.flatMap((season) => season.teams.map((team) => [team.entityId, team.name] as const)),
      ),
    [seasons],
  );
  const orphanIds = value.filter((entityId) => !loadedTeamIds.has(entityId));
  const orphanNamesQuery = useEntitiesListQuery({
    sport,
    organizationId,
    seasonIds: [],
    enabled: !disabled && orphanIds.length > 0,
  });
  const orphanTeams = useMemo((): SeasonTeam[] => {
    const namesById = new Map(
      (orphanNamesQuery.data ?? []).map((entity) => [String(entity.entityId), entity.name?.trim() ?? ""]),
    );
    return orphanIds.map((entityId) => ({
      entityId,
      name: loadedTeamNamesById.get(entityId) || namesById.get(entityId) || "",
    }));
  }, [loadedTeamNamesById, orphanIds, orphanNamesQuery.data]);

  useEffect(() => {
    const resolvedEntries = Object.entries(pendingSelectAll).filter(([seasonId]) => {
      const season = seasons.find((group) => group.seasonId === seasonId);
      return Boolean(season) && !season?.isLoading;
    });
    if (resolvedEntries.length === 0) {
      return;
    }

    let nextIds = value;
    const nextPending = { ...pendingSelectAll };
    for (const [seasonId, shouldSelect] of resolvedEntries) {
      const season = seasons.find((group) => group.seasonId === seasonId);
      const teamIds = season?.teams.map((team) => team.entityId) ?? [];
      nextIds = shouldSelect ? addSelectedIds(nextIds, teamIds) : removeSelectedIds(nextIds, teamIds);
      delete nextPending[seasonId];
    }
    onChange(nextIds);
    setPendingSelectAll(nextPending);
  }, [onChange, pendingSelectAll, seasons, value]);

  const filteredSeasons = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return seasons;
    }

    return seasons
      .map((season) => {
        const seasonMatches = season.name.toLowerCase().includes(query);
        return {
          ...season,
          teams: seasonMatches
            ? season.teams
            : season.teams.filter((team) => team.name.toLowerCase().includes(query)),
        };
      })
      .filter((season) => season.name.toLowerCase().includes(query) || season.teams.length > 0);
  }, [seasons, search]);

  const filteredOrphanTeams = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return orphanTeams;
    }
    return orphanTeams.filter((team) => team.name.toLowerCase().includes(query));
  }, [orphanTeams, search]);

  const seasonIds = filteredSeasons.map((season) => season.seasonId);
  const allExpanded = seasonIds.length > 0 && seasonIds.every((id) => expandedIds.has(id));
  const allCollapsed = seasonIds.length === 0 || seasonIds.every((id) => !expandedIds.has(id));

  const setExpanded = (seasonId: string, expanded: boolean) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (expanded) {
        next.add(seasonId);
      } else {
        next.delete(seasonId);
      }
      return next;
    });
  };

  const handleToggleAll = (seasonId: string, checked: boolean, teamIds: string[], hasLoadedTeams: boolean) => {
    if (hasLoadedTeams) {
      onChange(checked ? addSelectedIds(value, teamIds) : removeSelectedIds(value, teamIds));
      return;
    }

    setExpanded(seasonId, true);
    setPendingSelectAll((current) => ({ ...current, [seasonId]: checked }));
  };

  if (disabled) {
    return (
      <p className="text-muted-foreground text-sm">{t("credentials.create.fields.select_competitions_first")}</p>
    );
  }

  if (treeQuery.isLoadingSeasons) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 text-sm">
        <Loader2Icon className="size-4 animate-spin" />
        {t("credentials.create.fields.loading_teams")}
      </div>
    );
  }

  if (seasons.length === 0) {
    return <p className="text-muted-foreground text-sm">{t("credentials.create.fields.no_seasons")}</p>;
  }

  return (
    <div className="overflow-hidden rounded-md border bg-white dark:bg-transparent">
      <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t("credentials.create.fields.search_teams")}
          className="h-8 max-w-xs"
        />
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={allExpanded}
            onClick={() => setExpandedIds(new Set(seasonIds))}
          >
            {t("credentials.create.fields.expand_all")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={allCollapsed}
            onClick={() => setExpandedIds(new Set())}
          >
            {t("credentials.create.fields.collapse_all")}
          </Button>
        </div>
      </div>

      {filteredSeasons.length === 0 && filteredOrphanTeams.length === 0 ? (
        <p className="text-muted-foreground p-4 text-sm">{t("credentials.create.fields.no_options")}</p>
      ) : (
        <ScrollArea className="h-80">
          <div className="divide-y divide-border/50">
            {filteredSeasons.map((season) => (
              <SeasonTeamNode
                key={season.seasonId}
                season={season}
                selectedIds={value}
                expanded={expandedIds.has(season.seasonId)}
                onExpandedChange={(expanded) => setExpanded(season.seasonId, expanded)}
                onToggleAll={(checked) =>
                  handleToggleAll(
                    season.seasonId,
                    checked,
                    season.teams.map((team) => team.entityId),
                    season.teams.length > 0,
                  )
                }
                onChange={onChange}
              />
            ))}
            {filteredOrphanTeams.length > 0 ? (
              <div className="odd:bg-muted/10 dark:odd:bg-white/3">
                <div className="flex items-center gap-2 py-2 pr-4 pl-10">
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">
                    {t("credentials.create.fields.other_selected_teams")}
                  </span>
                  <Badge variant="secondary" className="text-xs">
                    {t("credentials.create.fields.teams_selected", { count: filteredOrphanTeams.length })}
                  </Badge>
                </div>
                <div className="divide-y divide-border/50">
                  {filteredOrphanTeams.map((team) => (
                    <SeasonTeamRow
                      key={team.entityId}
                      team={team}
                      checkboxId={`season-team-other-${team.entityId}`}
                      checked={value.includes(team.entityId)}
                      onCheckedChange={(checked) =>
                        onChange(
                          checked
                            ? addSelectedIds(value, [team.entityId])
                            : removeSelectedIds(value, [team.entityId]),
                        )
                      }
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
